'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';

export type TerminalView = 'dashboard' | 'research' | 'market-data';

export interface TerminalShortcutHandlers {
  onFocusSearch: () => void;
  onEscape: () => void;
  onGoTo: (where: TerminalView) => void;
  onCycleWatchlist: (delta: 1 | -1) => void;
  onSetResearchTab?: (index: 0 | 1 | 2) => void;
  onSetMarketDataTab?: (index: number) => void;
  onShowHelp: () => void;
  view: TerminalView;
}

const G_SEQUENCE_TIMEOUT_MS = 1000;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function useTerminalShortcuts(handlers: TerminalShortcutHandlers) {
  // Keep handlers in a ref so the listener is stable across re-renders
  const ref = useRef(handlers);
  useLayoutEffect(() => {
    ref.current = handlers;
  });

  useEffect(() => {
    let gPending = false;
    let gTimer: ReturnType<typeof setTimeout> | null = null;
    const clearG = () => {
      gPending = false;
      if (gTimer) {
        clearTimeout(gTimer);
        gTimer = null;
      }
    };

    const onKey = (e: KeyboardEvent) => {
      const h = ref.current;
      const typing = isTypingTarget(e.target);

      // Inside inputs, only Escape works (to exit research / market-data views)
      if (typing) {
        if (e.key === 'Escape' && h.view !== 'dashboard') {
          e.preventDefault();
          h.onEscape();
        }
        return;
      }

      // Cmd/Ctrl-K → focus search (works regardless of modifiers state)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        h.onFocusSearch();
        return;
      }

      // Otherwise ignore modified keypresses (don't hijack Cmd-R, Cmd-T, etc.)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case '/':
          e.preventDefault();
          h.onFocusSearch();
          return;
        case 'Escape':
          if (h.view !== 'dashboard') {
            e.preventDefault();
            h.onEscape();
          }
          return;
        case '?':
          e.preventDefault();
          h.onShowHelp();
          return;
        case '[':
          e.preventDefault();
          h.onCycleWatchlist(-1);
          return;
        case ']':
          e.preventDefault();
          h.onCycleWatchlist(1);
          return;
        case '1':
        case '2':
        case '3':
          if (h.view === 'research' && h.onSetResearchTab) {
            e.preventDefault();
            h.onSetResearchTab((Number(e.key) - 1) as 0 | 1 | 2);
            return;
          }
          if (h.view === 'market-data' && h.onSetMarketDataTab) {
            e.preventDefault();
            h.onSetMarketDataTab(Number(e.key) - 1);
          }
          return;
        case '4':
        case '5':
        case '6':
        case '7':
          if (h.view === 'market-data' && h.onSetMarketDataTab) {
            e.preventDefault();
            h.onSetMarketDataTab(Number(e.key) - 1);
          }
          return;
        case 'g':
          gPending = true;
          if (gTimer) clearTimeout(gTimer);
          gTimer = setTimeout(clearG, G_SEQUENCE_TIMEOUT_MS);
          return;
        case 'd':
          if (gPending) {
            e.preventDefault();
            clearG();
            h.onGoTo('dashboard');
          }
          return;
        case 'r':
          if (gPending) {
            e.preventDefault();
            clearG();
            h.onGoTo('research');
          }
          return;
        case 'm':
          if (gPending) {
            e.preventDefault();
            clearG();
            h.onGoTo('market-data');
          }
          return;
        default:
          // Any other key cancels a pending `g`
          if (gPending && e.key.length === 1) clearG();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearG();
    };
  }, []);
}
